(() => {
  if (window.EmbryniumBundleState) return;

  const eventNames = Object.freeze({
    draftUpdated: 'embrynium:bundle:draft-updated',
    completed: 'embrynium:bundle:completed',
  });

  const propertyNames = Object.freeze({
    identity: '_Embrynium Bundle',
    label: 'Bundle',
    tier: 'Bundle Tier',
    source: '_Embrynium Source',
    operation: '_Embrynium Bundle ID',
  });

  class BundleState {
    constructor({ eligibleVariantIds = [], maxItems = 3, label = 'Embrynium Archive Bundle', storageKey = 'embrynium:bundle-draft:v1' } = {}) {
      this.eligibleVariantIds = new Set(eligibleVariantIds.map(String));
      this.maxItems = maxItems;
      this.label = label;
      this.storageKey = storageKey;
      this.draft = [];
    }

    isEligible(variantId) {
      return this.eligibleVariantIds.has(String(variantId));
    }

    summarize(cart) {
      const eligibleLines = (cart?.items || []).filter((item) => this.isEligible(item.variant_id));
      const eligibleQuantity = eligibleLines.reduce((total, item) => total + item.quantity, 0);
      const tier = eligibleQuantity >= 3 ? 3 : eligibleQuantity >= 2 ? 2 : 0;
      return { eligibleLines, eligibleQuantity, tier, cart };
    }

    hydrate(cart) {
      const draft = [];
      for (const item of cart?.items || []) {
        if (!this.isEligible(item.variant_id)) continue;
        for (let index = 0; index < item.quantity && draft.length < this.maxItems; index += 1) {
          draft.push({
            clientKey: `cart-${item.key}-${index}`,
            variantId: String(item.variant_id),
            productId: String(item.product_id || ''),
            productHandle: item.handle || '',
            title: item.product_title || item.title || 'Archive Piece',
            variantTitle: item.variant_title || '',
            selectedOptions: Array.isArray(item.variant_options) ? [...item.variant_options] : [],
            price: item.original_price ?? item.price ?? 0,
            image: item.image || '',
            url: item.url || '/cart',
            source: 'cart',
          });
        }
      }
      this.setDraft(draft, { source: 'cart' });
      return this.draft;
    }

    loadDraft() {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '[]');
        return Array.isArray(stored)
          ? stored.filter((item) => item?.variantId && this.isEligible(item.variantId)).slice(0, this.maxItems)
          : [];
      } catch {
        return [];
      }
    }

    clearStoredDraft() {
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch {
        // Storage can be unavailable in strict privacy modes.
      }
    }

    setDraft(items, { source = 'interface' } = {}) {
      this.draft = items
        .filter((item) => item?.variantId && this.isEligible(item.variantId))
        .slice(0, this.maxItems)
        .map((item) => ({ ...item, variantId: String(item.variantId) }));
      if (source !== 'cart') {
        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.draft));
        } catch {
          // The draft remains usable in memory when storage is unavailable.
        }
      }
      document.dispatchEvent(new CustomEvent(eventNames.draftUpdated, {
        detail: { draft: [...this.draft], source },
      }));
      return this.draft;
    }

    async replaceBundleLines(items = this.draft) {
      if (!window.EmbryniumCart) throw new Error('The shared cart service is unavailable.');
      this.setDraft(items);
      const currentCart = await window.EmbryniumCart.getCart();
      const oldBundleLines = currentCart.items.filter(
        (item) => item.properties?.[propertyNames.identity] === 'true' && this.isEligible(item.variant_id)
      );

      // Ordinary cart lines are authoritative and must never be duplicated just because
      // the shopper opened the bundle builder. Only add quantities that are missing
      // after counting existing non-bundle Archive items.
      const regularCounts = new Map();
      currentCart.items.forEach((item) => {
        if (!this.isEligible(item.variant_id) || item.properties?.[propertyNames.identity] === 'true') return;
        const id = String(item.variant_id);
        regularCounts.set(id, (regularCounts.get(id) || 0) + Number(item.quantity || 0));
      });

      const desiredCounts = new Map();
      this.draft.forEach(({ variantId }) => {
        const id = String(variantId);
        desiredCounts.set(id, (desiredCounts.get(id) || 0) + 1);
      });

      const requiredBundleCounts = new Map();
      desiredCounts.forEach((desired, id) => {
        const missing = Math.max(0, desired - (regularCounts.get(id) || 0));
        if (missing) requiredBundleCounts.set(id, missing);
      });

      if (!this.draft.length) {
        const removals = Object.fromEntries(oldBundleLines.map((item) => [item.key, 0]));
        return Object.keys(removals).length
          ? window.EmbryniumCart.updateLines(removals, { source: 'bundle-clear' })
          : currentCart;
      }

      const eventualEligibleQuantity = [...regularCounts.values()].reduce((sum, qty) => sum + qty, 0)
        + [...requiredBundleCounts.values()].reduce((sum, qty) => sum + qty, 0);
      const tier = eventualEligibleQuantity >= 3 ? '3 Shirt Bundle' : '2 Shirt Bundle';
      const properties = {
        [propertyNames.identity]: 'true',
        [propertyNames.label]: this.label,
        [propertyNames.tier]: tier,
        [propertyNames.source]: 'Bundle Builder',
        [propertyNames.operation]:
          window.crypto?.randomUUID?.() || `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      };

      let cart = currentCart;
      if (requiredBundleCounts.size) {
        cart = await window.EmbryniumCart.addItems(
          [...requiredBundleCounts].map(([id, quantity]) => ({ id, quantity, properties })),
          { source: 'bundle-builder', notify: oldBundleLines.length === 0 }
        );
      }

      if (oldBundleLines.length) {
        const removals = Object.fromEntries(oldBundleLines.map((item) => [item.key, 0]));
        try {
          cart = await window.EmbryniumCart.updateLines(removals, { source: 'bundle-reconcile' });
        } catch (error) {
          // If replacing old bundle lines fails, remove only this operation's newly added lines.
          const rollbackCart = await window.EmbryniumCart.getCart().catch(() => null);
          const rollback = Object.fromEntries(
            (rollbackCart?.items || [])
              .filter((item) => item.properties?.[propertyNames.operation] === properties[propertyNames.operation])
              .map((item) => [item.key, 0])
          );
          if (Object.keys(rollback).length) {
            await window.EmbryniumCart.updateLines(rollback, {
              source: 'bundle-reconcile-rollback',
              notify: false,
            }).catch(() => null);
          }
          throw error;
        }
      }

      // Refresh after reconciliation so every surface receives Shopify's authoritative cart.
      cart = await window.EmbryniumCart.getCart();
      document.dispatchEvent(new CustomEvent(eventNames.completed, {
        detail: { cart, draft: [...this.draft], tier },
      }));
      this.clearStoredDraft();
      return cart;
    }
  }

  window.EmbryniumBundleState = Object.freeze({ BundleState, eventNames, propertyNames });
})();
